import type { Document } from "../document/Document";
import type { Layer } from "../document/Layer";
import type { Command } from "./Command";
import { findLayer } from "../document/LayerUtils";
import { setPos } from "./MoveLayerCommand";

// AlignLayersCommand — Layer ▸ Align ▸ {Left/H-Center/Right/Top/V-Center/Bottom}
// Edges/Centers, and the matching row of 6 buttons in the Move tool options
// bar (audit i2).
//
// Photoshop semantics implemented here:
//   - 2+ target layers: every layer is repositioned so the chosen edge/center
//     aligns to the UNION bounding box of all target layers (the box doesn't
//     move; layers slide to meet it).
//   - Exactly 1 target layer: Photoshop aligns that single layer to the
//     CANVAS instead (there's no "selection" to align to) — see
//     planAlignLayers below.
//   - 0 targets: no-op (planAlignLayers returns null).
//
// Shaped like MoveLayersCommand (per-layer absolute from/to, one atomic step,
// invert() swaps from/to) so undo reverts every moved layer in one Ctrl+Z.

/** The 6 Photoshop align operations. Left/H-Center/Right move along X only;
 * Top/V-Center/Bottom move along Y only. */
export type AlignOp = "left" | "h-center" | "right" | "top" | "v-center" | "bottom";

/** A single layer's absolute from/to move, one entry per affected layer. */
export interface LayerAlignEntry {
  readonly layerId: string;
  readonly fromX: number;
  readonly fromY: number;
  readonly toX: number;
  readonly toY: number;
}

/**
 * A layer's content bounding box in document space. `{x, y}` is the
 * top-left corner — NOT necessarily the layer's own `x`/`y` field. Groups in
 * particular track `x`/`y` as a move-delta accumulator that starts at (0, 0)
 * regardless of where their children actually sit (see GroupLayersCommand /
 * MoveLayerCommand's `setPos`), so the group's *content* box has to be
 * derived from its children instead.
 */
export interface LayerRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * Resolve a layer's content bounding box for Align/Distribute math. Mirrors
 * the per-kind rules `Canvas2DRenderer.layerBBox` uses at render time (so
 * alignment lines up with what's actually drawn) but stays DOM-free:
 * point-text type layers (no `textBounds`) can't be measured without a
 * canvas 2D context the way the renderer does it, so they fall back to a
 * zero-size box at the layer's own position rather than pulling that
 * DOM-dependent measurement path into a pure Command.
 *
 * Known pre-existing gap shared with MoveLayerCommand/MoveTool: Artboard
 * layers track their rect via `artboardX/Y/Width/Height`, not `x/y`, while
 * every move/align in this codebase (including the Move tool's own drag)
 * only ever writes `x`/`y`. Aligning an artboard therefore updates its
 * tracking `x`/`y` field but has no visual effect — the same limitation the
 * Move tool already has today, not something introduced here.
 */
export function getLayerAlignRect(layer: Layer, doc: Document): LayerRect {
  switch (layer.kind) {
    case "group": {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const child of layer.children) {
        const r = getLayerAlignRect(child, doc);
        if (r.w <= 0 && r.h <= 0) continue;
        if (r.x < minX) minX = r.x;
        if (r.y < minY) minY = r.y;
        if (r.x + r.w > maxX) maxX = r.x + r.w;
        if (r.y + r.h > maxY) maxY = r.y + r.h;
      }
      if (!Number.isFinite(minX)) return { x: layer.x, y: layer.y, w: 0, h: 0 };
      return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    }
    case "adjustment":
    case "solid-fill":
    case "gradient-fill":
    case "pattern-fill":
      // These fill/adjust the whole canvas regardless of x/y (matches
      // Canvas2DRenderer.layerBBox's treatment of the same kinds).
      return { x: 0, y: 0, w: doc.width, h: doc.height };
    case "smart-object":
      return {
        x: layer.x,
        y: layer.y,
        w: layer.embedded.width * layer.transform.scaleX,
        h: layer.embedded.height * layer.transform.scaleY,
      };
    case "linked-smart-object":
      return {
        x: layer.x,
        y: layer.y,
        w: (layer.composite?.width ?? 0) * layer.transform.scaleX,
        h: (layer.composite?.height ?? 0) * layer.transform.scaleY,
      };
    case "shape": {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const sp of layer.path.subpaths) {
        for (const pt of sp.points) {
          if (pt.x < minX) minX = pt.x;
          if (pt.y < minY) minY = pt.y;
          if (pt.x > maxX) maxX = pt.x;
          if (pt.y > maxY) maxY = pt.y;
        }
      }
      if (!Number.isFinite(minX)) return { x: layer.x, y: layer.y, w: 0, h: 0 };
      const pad = layer.strokeWidth / 2;
      return {
        x: minX - pad + layer.x,
        y: minY - pad + layer.y,
        w: (maxX - minX) + pad * 2,
        h: (maxY - minY) + pad * 2,
      };
    }
    case "artboard":
      return { x: layer.artboardX, y: layer.artboardY, w: layer.artboardWidth, h: layer.artboardHeight };
    case "frame":
      return { x: layer.x, y: layer.y, w: layer.frameWidth, h: layer.frameHeight };
    case "video-frame":
      return { x: layer.x, y: layer.y, w: layer.videoWidth, h: layer.videoHeight };
    case "3d":
      return { x: layer.x, y: layer.y, w: layer.composite?.width ?? 0, h: layer.composite?.height ?? 0 };
    case "type":
      if (layer.textBounds) {
        return {
          x: layer.x + layer.textBounds.x,
          y: layer.y + layer.textBounds.y,
          w: layer.textBounds.w,
          h: layer.textBounds.h,
        };
      }
      // Point text: no DOM measurement available in a pure Command.
      return { x: layer.x, y: layer.y, w: 0, h: 0 };
    case "raster":
    case "background":
      return { x: layer.x, y: layer.y, w: layer.pixels.width, h: layer.pixels.height };
    default:
      return { x: 0, y: 0, w: 0, h: 0 };
  }
}

/** Layers that are hidden, fully locked, or position-locked don't move —
 * the same guard the Move tool applies to "extra" selected/linked layers
 * (see MoveTool.resolveExtraStarts). */
function isAlignable(layer: Layer): boolean {
  return layer.visible && !layer.lockAll && !layer.lockPosition;
}

/**
 * Union the active layer id with the Layers-panel multi-selection into one
 * de-duplicated id list — the standard "which layers does this multi-layer
 * op target" resolution used across the app (mirrors
 * `transformLayerOrientationIds` in main.ts, minus its raster-only filter:
 * Align/Distribute reposition layers of any kind, same as the Move tool).
 * Shared by the Layer ▸ Align/Distribute menu items and the Move tool
 * options-bar align buttons so both paths agree on the target set.
 */
export function collectLayerTargetIds(
  activeLayerId: string | null,
  selectedLayerIds: ReadonlySet<string>,
): string[] {
  const ids = new Set<string>();
  if (activeLayerId) ids.add(activeLayerId);
  for (const id of selectedLayerIds) ids.add(id);
  return [...ids];
}

/**
 * Build the AlignLayersCommand for `op` over `layerIds`, or null when there's
 * nothing to align (no resolvable/alignable target layers).
 *
 * - 2+ resolvable layers: aligns to the union of their content boxes.
 * - Exactly 1: aligns that layer to the canvas (Photoshop's single-layer
 *   Align behaviour — audit i2).
 */
export function planAlignLayers(doc: Document, layerIds: readonly string[], op: AlignOp): AlignLayersCommand | null {
  const seen = new Set<string>();
  const targets: Layer[] = [];
  for (const id of layerIds) {
    if (seen.has(id)) continue;
    seen.add(id);
    const l = findLayer(doc.layers, id);
    if (l && isAlignable(l)) targets.push(l);
  }
  if (targets.length === 0) return null;

  const rects = targets.map((l) => getLayerAlignRect(l, doc));

  let bbox: LayerRect;
  if (targets.length >= 2) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const r of rects) {
      if (r.x < minX) minX = r.x;
      if (r.y < minY) minY = r.y;
      if (r.x + r.w > maxX) maxX = r.x + r.w;
      if (r.y + r.h > maxY) maxY = r.y + r.h;
    }
    bbox = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  } else {
    // Single layer selected: align to the canvas instead of a selection.
    bbox = { x: 0, y: 0, w: doc.width, h: doc.height };
  }

  const entries: LayerAlignEntry[] = [];
  for (let i = 0; i < targets.length; i++) {
    const layer = targets[i]!;
    const r = rects[i]!;
    let dx = 0;
    let dy = 0;
    switch (op) {
      case "left": dx = bbox.x - r.x; break;
      case "h-center": dx = (bbox.x + bbox.w / 2) - (r.x + r.w / 2); break;
      case "right": dx = (bbox.x + bbox.w) - (r.x + r.w); break;
      case "top": dy = bbox.y - r.y; break;
      case "v-center": dy = (bbox.y + bbox.h / 2) - (r.y + r.h / 2); break;
      case "bottom": dy = (bbox.y + bbox.h) - (r.y + r.h); break;
    }
    dx = Math.round(dx);
    dy = Math.round(dy);
    entries.push({
      layerId: layer.id,
      fromX: layer.x,
      fromY: layer.y,
      toX: layer.x + dx,
      toY: layer.y + dy,
    });
  }
  return new AlignLayersCommand(entries);
}

export class AlignLayersCommand implements Command {
  readonly kind = "align-layers";

  constructor(private readonly moves: readonly LayerAlignEntry[]) {}

  apply(doc: Document): Document {
    let layers = doc.layers;
    for (const m of this.moves) {
      layers = setPos(layers, m.layerId, m.toX, m.toY);
    }
    return { ...doc, layers };
  }

  invert(_before: Document, _after: Document): Command {
    return new AlignLayersCommand(
      this.moves.map((m) => ({
        layerId: m.layerId,
        fromX: m.toX,
        fromY: m.toY,
        toX: m.fromX,
        toY: m.fromY,
      })),
    );
  }
}
